import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Mail, Phone } from "lucide-react";
import { ReturnPolicyBody } from "@/components/ReturnPolicy";
import { RETURN_POLICY_META } from "@/lib/returnPolicy";

const setMeta = (attr, key, content) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

export default function ReturnPolicyPage() {
  useEffect(() => {
    document.title = `${RETURN_POLICY_META.title} | Pickle Bullpen`;
    const desc = `${RETURN_POLICY_META.windowDays}-day returns on unused Pro Shop gear, free returns for defective or incorrect items, refunds to your original payment method. Read the full Pickle Bullpen return, refund and exchange policy.`;
    setMeta("name", "description", desc);
    setMeta("property", "og:title", RETURN_POLICY_META.title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", RETURN_POLICY_META.url);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", RETURN_POLICY_META.url);
    let s = document.getElementById("return-policy-ld");
    if (!s) { s = document.createElement("script"); s.id = "return-policy-ld"; s.type = "application/ld+json"; document.head.appendChild(s); }
    s.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "MerchantReturnPolicy", name: RETURN_POLICY_META.title, url: RETURN_POLICY_META.url,
      applicableCountry: "US", returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: RETURN_POLICY_META.windowDays, returnMethod: "https://schema.org/ReturnByMail",
      returnFees: "https://schema.org/ReturnFeesCustomerResponsibility", refundType: "https://schema.org/FullRefund",
      itemCondition: "https://schema.org/NewCondition",
    });
    return () => document.getElementById("return-policy-ld")?.remove();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-5 sm:px-8 py-10" data-testid="return-policy-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-8 print:hidden">
          <Link to="/" data-testid="return-policy-back" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 hover:text-lime-400"><ArrowLeft className="w-3.5 h-3.5" /> Pickle Bullpen</Link>
          <button data-testid="return-policy-print" onClick={() => window.print()} className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-zinc-300"><Printer className="w-3.5 h-3.5" /> Print / Save PDF</button>
        </div>
        <p className="font-mono text-lime-300 uppercase tracking-[0.3em] text-xs mb-3">Pickle Bullpen Pro Shop · Effective {RETURN_POLICY_META.effective}</p>
        <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl mb-6" data-testid="return-policy-title">Return, Refund &amp; Exchange Policy</h1>
        <ReturnPolicyBody testid="return-policy-full" />
        <div className="mt-10 border-t border-zinc-800 pt-6 flex flex-wrap gap-5 text-xs text-zinc-400 print:hidden">
          <a href={`mailto:${RETURN_POLICY_META.contact}?subject=Return%20request`} data-testid="return-policy-email" className="inline-flex items-center gap-1.5 hover:text-white"><Mail className="w-3.5 h-3.5 text-lime-400" /> {RETURN_POLICY_META.contact}</a>
          <a href={`tel:${RETURN_POLICY_META.phone.replace(/\D/g, "")}`} data-testid="return-policy-phone" className="inline-flex items-center gap-1.5 hover:text-white"><Phone className="w-3.5 h-3.5 text-lime-400" /> {RETURN_POLICY_META.phone}</a>
        </div>
      </div>
    </div>
  );
}
